import { useCallback, useEffect, useState } from "react";
import "./Resortbookings.css";

const API_BASE =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_API_URL) ||
  "https://bmtvendorvendorfinal.onrender.com";

const LIMIT = 15;

const STATUS_TABS = [
  ["", "All"],
  ["PENDING", "Pending"],
  ["CONFIRMED", "Confirmed"],
  ["CHECKED_IN", "Checked in"],
  ["CHECKED_OUT", "Checked out"],
  ["CANCELLED", "Cancelled"],
  ["NO_SHOW", "No show"],
];

const STATUS_LABEL = Object.fromEntries(
  STATUS_TABS.filter(([status]) => status)
);

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (value) => {
  if (!value) return "-";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const fmtDateTime = (value) => {
  if (!value) return "-";

  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const guestCount = (booking) => {
  const adults = Number(booking?.adults || 0);
  const children = Number(booking?.children || 0);

  return `${adults} adult${adults === 1 ? "" : "s"}${
    children
      ? `, ${children} child${children === 1 ? "" : "ren"}`
      : ""
  }`;
};

async function api(path, { params } = {}) {
  const token = localStorage.getItem("vendorToken");

  if (!token) {
    throw new Error(
      "Vendor token nahi mila. Please vendor panel mein dobara login karo."
    );
  }

  const url = new URL(
    `${API_BASE.replace(/\/$/, "")}/api/resort-bookings${path}`
  );

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  let response;

  try {
    response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
  } catch {
    throw new Error(
      "Backend server se connection nahi ho raha. Server aur API URL check karo."
    );
  }

  let data = {};

  try {
    data = await response.json();
  } catch {
    // Response JSON nahi hai.
  }

  if (!response.ok || data.success === false) {
    throw new Error(
      data.message || `Request failed with status ${response.status}.`
    );
  }

  return data;
}

function StatusPill({ status }) {
  const value = String(status || "UNKNOWN").toUpperCase();

  return (
    <span className={`rb-pill rb-s-${value.toLowerCase()}`}>
      {STATUS_LABEL[value] || value.replaceAll("_", " ")}
    </span>
  );
}

function PayPill({ status }) {
  const value = String(status || "PENDING").toUpperCase();

  return (
    <span className={`rb-pill rb-p-${value.toLowerCase()}`}>
      {value.replaceAll("_", " ")}
    </span>
  );
}

function InfoRow({ label, children }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children ?? "-"}</dd>
    </div>
  );
}

function BookingDetails({ booking, onClose }) {
  const [details, setDetails] = useState(booking);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadDetails() {
      if (!booking?._id) return;

      setLoading(true);
      setError("");

      try {
        const data = await api(`/${booking._id}`);

        if (active && data.booking) {
          setDetails(data.booking);
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadDetails();

    return () => {
      active = false;
    };
  }, [booking?._id]);

  const b = details || {};
  const pricing = b.pricing || {};

  return (
    <div
      className="rb-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="rb-modal wide"
        role="dialog"
        aria-modal="true"
        aria-label="Booking details"
      >
        <div className="rb-modal-head">
          <div>
            <h2>{b.bookingId || "Booking details"}</h2>
            <p>
              {b.resort?.name || "Resort"} · Created{" "}
              {fmtDateTime(b.createdAt)}
            </p>
          </div>

          <button
            type="button"
            className="rb-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="rb-modal-body">
          {loading && <p className="rb-muted">Loading booking details...</p>}

          {error && <div className="rb-alert error">{error}</div>}

          <div className="rb-detail-status">
            <StatusPill status={b.bookingStatus} />
            <PayPill status={b.paymentStatus} />
            <span className="rb-muted">
              Source: {String(b.bookingSource || "-").replaceAll("_", " ")}
            </span>
          </div>

          <div className="rb-detail-grid">
            <section className="rb-block">
              <h3>Stay details</h3>

              <dl className="rb-kv">
                <InfoRow label="Resort">
                  {b.resort?.name || "-"}
                </InfoRow>

                <InfoRow label="Room category">
                  {b.roomCategory?.name || "-"}
                </InfoRow>

                <InfoRow label="Rate plan">
                  {b.ratePlan?.displayName || "-"}
                </InfoRow>

                <InfoRow label="Room number">
                  {b.roomUnit?.roomNumber || "Not assigned"}
                </InfoRow>

                <InfoRow label="Check-in">
                  {fmtDate(b.checkIn)}
                </InfoRow>

                <InfoRow label="Check-out">
                  {fmtDate(b.checkOut)}
                </InfoRow>

                <InfoRow label="Nights">
                  {b.totalNights ?? "-"}
                </InfoRow>

                <InfoRow label="Rooms">
                  {b.rooms ?? "-"}
                </InfoRow>

                <InfoRow label="Guests">
                  {guestCount(b)}
                </InfoRow>

                <InfoRow label="Checked in at">
                  {fmtDateTime(b.checkedInAt)}
                </InfoRow>

                <InfoRow label="Checked out at">
                  {fmtDateTime(b.checkedOutAt)}
                </InfoRow>
              </dl>
            </section>

            <section className="rb-block">
              <h3>Guest details</h3>

              <dl className="rb-kv">
                <InfoRow label="Name">
                  {b.leadGuest?.name || "-"}
                </InfoRow>

                <InfoRow label="Phone">
                  {b.leadGuest?.phone || "-"}
                </InfoRow>

                <InfoRow label="Email">
                  {b.leadGuest?.email || "-"}
                </InfoRow>
              </dl>

              {Array.isArray(b.additionalGuests) &&
                b.additionalGuests.length > 0 && (
                  <>
                    <h3>Additional guests</h3>

                    <ul className="rb-plain-list">
                      {b.additionalGuests.map((guest, index) => (
                        <li key={guest._id || index}>
                          {guest.name || "Guest"} · {guest.phone || "-"}
                          {guest.age ? ` · ${guest.age} yrs` : ""}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

              {b.specialRequest && (
                <p className="rb-note">
                  <strong>Special request:</strong> {b.specialRequest}
                </p>
              )}

              {b.cancellationReason && (
                <p className="rb-note">
                  <strong>Cancellation reason:</strong>{" "}
                  {b.cancellationReason}
                </p>
              )}
            </section>

            <section className="rb-block">
              <h3>Payment summary</h3>

              <dl className="rb-lines">
                <InfoRow label="Room charges">
                  {money(pricing.roomPrice)}
                </InfoRow>

                <InfoRow label="Extra adult charges">
                  {money(pricing.extraAdultAmount)}
                </InfoRow>

                <InfoRow label="Extra child charges">
                  {money(pricing.extraChildAmount)}
                </InfoRow>

                <InfoRow label="Discount">
                  {money(pricing.discountAmount)}
                </InfoRow>

                <InfoRow label="GST">
                  {money(pricing.gstAmount)}
                </InfoRow>

                <InfoRow label="Total amount">
                  <strong>{money(pricing.totalAmount)}</strong>
                </InfoRow>

                <InfoRow label="Paid amount">
                  {money(b.paidAmount)}
                </InfoRow>

                <InfoRow label="Refund amount">
                  {money(b.refundAmount)}
                </InfoRow>

                <InfoRow label="Payment method">
                  {String(b.paymentMethod || "-").replaceAll("_", " ")}
                </InfoRow>
              </dl>

              {Array.isArray(b.payments) && b.payments.length > 0 && (
                <>
                  <h3>Payment history</h3>

                  <ul className="rb-plain-list">
                    {b.payments.map((payment, index) => (
                      <li key={payment._id || index}>
                        {money(payment.amount)} ·{" "}
                        {String(payment.method || "-").replaceAll("_", " ")}
                        {" · "}
                        {fmtDateTime(payment.paidAt)}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          </div>

          <div className="rb-actions">
            <button
              type="button"
              className="rb-btn ghost"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ResortBookings() {
  const [resorts, setResorts] = useState([]);
  const [resortId, setResortId] = useState("");
  const [statusTab, setStatusTab] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadOptions = useCallback(async () => {
    try {
      const data = await api("/options");
      setResorts(data.resorts || []);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await api("", {
        params: {
          resortId,
          status: statusTab,
          paymentStatus,
          search,
          dateFrom,
          dateTo,
          page,
          limit: LIMIT,
        },
      });

      setBookings(data.bookings || []);
      setTotal(Number(data.total || 0));
      setTotalPages(Math.max(1, Number(data.totalPages || 1)));
    } catch (err) {
      setBookings([]);
      setTotal(0);
      setTotalPages(1);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    resortId,
    statusTab,
    paymentStatus,
    search,
    dateFrom,
    dateTo,
    page,
  ]);

  const loadStats = useCallback(async () => {
    try {
      const data = await api("/stats", {
        params: { resortId },
      });

      setStats(data.stats || null);
    } catch (err) {
      setStats(null);
      setError(err.message);
    }
  }, [resortId]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const resetFilters = () => {
    setResortId("");
    setStatusTab("");
    setPaymentStatus("");
    setSearchInput("");
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasFilters =
    Boolean(resortId) ||
    Boolean(statusTab) ||
    Boolean(paymentStatus) ||
    Boolean(search) ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const statCards = [
    ["Check-ins today", stats?.todayCheckIns],
    ["Check-outs today", stats?.todayCheckOuts],
    ["In house", stats?.inHouse],
    ["Upcoming", stats?.upcoming],
    ["Revenue", money(stats?.revenue)],
    ["Balance due", money(stats?.due)],
  ];

  return (
    <div className="rb-page">
      <header className="rb-header">
        <div>
          <h1>Resort bookings</h1>
          <p>View reservations, guest details and payment information.</p>
        </div>

        <div className="rb-header-actions">
          <select
            className="rb-select"
            value={resortId}
            onChange={(event) => {
              setResortId(event.target.value);
              setPage(1);
            }}
            aria-label="Filter by resort"
          >
            <option value="">All resorts</option>

            {resorts.map((resort) => (
              <option key={resort._id} value={resort._id}>
                {resort.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="rb-btn ghost"
            onClick={() => {
              loadBookings();
              loadStats();
            }}
          >
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="rb-alert error">
          {error}
        </div>
      )}

      {stats && (
        <div className="rb-stats">
          {statCards.map(([label, value]) => (
            <div className="rb-stat" key={label}>
              <span>{label}</span>
              <strong>{value ?? 0}</strong>
            </div>
          ))}
        </div>
      )}

      <nav className="rb-tabs" aria-label="Booking status">
        {STATUS_TABS.map(([value, label]) => {
          const count = value
            ? stats?.statusCounts?.[value]
            : stats?.total;

          return (
            <button
              type="button"
              key={value || "all"}
              className={`rb-tab ${statusTab === value ? "active" : ""}`}
              onClick={() => {
                setStatusTab(value);
                setPage(1);
              }}
            >
              {label}
              {count !== undefined && count !== null && (
                <em>{count}</em>
              )}
            </button>
          );
        })}
      </nav>

      <div className="rb-filters">
        <input
          className="rb-search"
          placeholder="Search booking ID, guest name, phone or email"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />

        <select
          value={paymentStatus}
          onChange={(event) => {
            setPaymentStatus(event.target.value);
            setPage(1);
          }}
          aria-label="Filter payment status"
        >
          <option value="">All payments</option>
          <option value="PENDING">Pending</option>
          <option value="PARTIAL">Partial</option>
          <option value="PAID">Paid</option>
          <option value="REFUNDED">Refunded</option>
        </select>

        <label className="rb-date-filter">
          <span>Check-in from</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(1);
            }}
          />
        </label>

        <label className="rb-date-filter">
          <span>To</span>
          <input
            type="date"
            min={dateFrom || undefined}
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(1);
            }}
          />
        </label>

        {hasFilters && (
          <button
            type="button"
            className="rb-btn ghost small"
            onClick={resetFilters}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="rb-table-wrap">
        <table className="rb-table">
          <thead>
            <tr>
              <th>Booking</th>
              <th>Guest</th>
              <th>Room</th>
              <th>Stay</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan="7" className="rb-empty">
                  Loading bookings...
                </td>
              </tr>
            )}

            {!loading && bookings.length === 0 && (
              <tr>
                <td colSpan="7" className="rb-empty">
                  {hasFilters
                    ? "No bookings match these filters."
                    : "No bookings found."}
                </td>
              </tr>
            )}

            {!loading &&
              bookings.map((booking) => (
                <tr
                  key={booking._id}
                  onClick={() => setSelected(booking)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <strong>{booking.bookingId || "-"}</strong>
                    <small>{fmtDateTime(booking.createdAt)}</small>
                  </td>

                  <td>
                    <strong>{booking.leadGuest?.name || "-"}</strong>
                    <small>{booking.leadGuest?.phone || "-"}</small>
                  </td>

                  <td>
                    <strong>{booking.roomCategory?.name || "-"}</strong>
                    <small>
                      {booking.resort?.name || "-"}
                      {booking.roomUnit?.roomNumber
                        ? ` · Room ${booking.roomUnit.roomNumber}`
                        : ""}
                    </small>
                  </td>

                  <td>
                    <strong>
                      {fmtDate(booking.checkIn)} to{" "}
                      {fmtDate(booking.checkOut)}
                    </strong>

                    <small>
                      {booking.totalNights || 0} night(s) ·{" "}
                      {booking.rooms || 0} room(s) ·{" "}
                      {guestCount(booking)}
                    </small>
                  </td>

                  <td>
                    <strong>
                      {money(booking.pricing?.totalAmount)}
                    </strong>
                    <small>
                      Paid {money(booking.paidAmount)}
                    </small>
                  </td>

                  <td>
                    <div className="rb-pills">
                      <StatusPill status={booking.bookingStatus} />
                      <PayPill status={booking.paymentStatus} />
                    </div>
                  </td>

                  <td>
                    <button
                      type="button"
                      className="rb-btn ghost small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelected(booking);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <footer className="rb-pager">
        <span>
          {total} booking{total === 1 ? "" : "s"}
        </span>

        <div>
          <button
            type="button"
            className="rb-btn ghost small"
            disabled={page <= 1 || loading}
            onClick={() => setPage((current) => current - 1)}
          >
            Previous
          </button>

          <span className="rb-page-num">
            Page {page} of {totalPages}
          </span>

          <button
            type="button"
            className="rb-btn ghost small"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </button>
        </div>
      </footer>

      {selected && (
        <BookingDetails
          key={selected._id}
          booking={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}